import { useLocale } from '../locale/LocaleContext.js'
import type { MessageKey } from '../i18n/index.js'

// User level (1-6) → a named mountain + hand-drawn silhouette. The level number
// is the source of truth (from the API); the mountain naming/iconography is a
// frontend concern, chosen to ascend in altitude with the level. Silhouettes are
// stylised (distinct shapes), not photoreal portraits of the named peaks.

const MIN_LEVEL = 1
const MAX_LEVEL = 6

function clampLevel(level: number): number {
  if (!Number.isFinite(level)) return MIN_LEVEL
  return Math.min(MAX_LEVEL, Math.max(MIN_LEVEL, Math.round(level)))
}

// Level → name i18n key. A switch (not array indexing) keeps the return a
// concrete MessageKey with no `undefined` under noUncheckedIndexedAccess.
function levelNameKey(level: number): MessageKey {
  switch (clampLevel(level)) {
    case 1:
      return 'meLevelName1'
    case 2:
      return 'meLevelName2'
    case 3:
      return 'meLevelName3'
    case 4:
      return 'meLevelName4'
    case 5:
      return 'meLevelName5'
    default:
      return 'meLevelName6'
  }
}

// Each silhouette is drawn in a 24×24 box on a y=21 baseline, filled with
// currentColor. Shapes are deliberately distinct so they read at chip size:
// spire cluster / stepped cliff / long low ridge / jagged fan / sharp pyramid /
// broad massif.
const SILHOUETTE_PATHS: Record<number, string> = {
  1: 'M1 21 L5 9 L7 21 Z M8 21 L12 4 L16 21 Z M15 21 L19 11 L23 21 Z', // 黄山 spires
  2: 'M4 21 L4 9 L10 9 L10 5 L16 5 L16 12 L20 12 L20 21 Z', // 华山 stepped cliff
  3: 'M2 21 L2 16 L7 12 L11 15 L15 11 L20 14 L22 13 L22 21 Z', // 苍山 long ridge
  4: 'M2 21 L5 12 L8 17 L11 7 L14 16 L17 10 L20 17 L22 14 L22 21 Z', // 玉龙 jagged fan
  5: 'M2 21 L9 9 L12 3 L15 9 L22 21 Z', // 卡瓦格博 sharp pyramid
  6: 'M1 21 L7 11 L13 5 L15 8 L23 21 Z', // 珠峰 broad massif
}

export function MountainSilhouette({
  level,
  className = '',
}: {
  level: number
  className?: string
}) {
  const lvl = clampLevel(level)
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <path d={SILHOUETTE_PATHS[lvl]} fill="currentColor" />
    </svg>
  )
}

export function levelName(level: number, t: (key: MessageKey) => string): string {
  return t(levelNameKey(level))
}

export function LevelChip({ level }: { level: number }) {
  const { t } = useLocale()
  const lvl = clampLevel(level)
  const name = levelName(lvl, t)
  return (
    <span
      className="inline-flex items-center gap-1 rounded-[var(--xr-radius-sm)] border border-[var(--xr-brand-primary)]/20 bg-[var(--xr-brand-primary-soft)] px-2 py-0.5 text-xs font-medium text-[var(--xr-brand-primary)]"
      data-testid="me-profile-level"
      title={`${t('meSummaryLevel')} ${lvl} · ${name}`}
    >
      <MountainSilhouette level={lvl} className="h-3.5 w-3.5" />
      <span>{name}</span>
    </span>
  )
}
